import "./polyfills";
import "./WindowGlobalFix";
import "./App.css";
import "./index.css";

import { ApolloProvider } from "@apollo/client";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter5Adapter } from "use-query-params/adapters/react-router-5";
import { parse, stringify } from "query-string";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { asyncWithLDProvider, LDEvaluationDetail } from "launchdarkly-react-client-sdk";
import { createRoot } from "react-dom/client";
import {
  EventInterceptionPlugin,
  FlagOverridePlugin,
  useLaunchDarklyToolbar,
} from "@launchdarkly/toolbar";

import "./App.css";
import { ErrorBoundary } from "./components/errorBoundary";
import App from "./containers/app";
import { GraphQlClient } from "./graphql/client";
import { I18nProvider } from "./appV2/i18n/I18nProvider";
import "./index.css";
import store from "./store";
import meta from "./utils/meta";

import { ChatProvider } from "./containers/chat/chat";
import { QueryProvider } from "./providers/QueryProvider";

import { routes } from "@src/appV2/redesign/utils/routes";
import { Guest } from "./guest";
import { MaterialUiProviders } from "./mui/providers/MaterialUiProviders";
import { loadLazily } from "./utils/loadLazily";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { FallbackLoader } from "./components/lazyLoad";
import { environmentConfig, isDevelopmentNodeEnvironment } from "./appV2/environment";
import { Helmet } from "react-helmet";
import { LD_USER_KIND } from "./appV2/FeatureFlags/cbhUserContext";
import { ImpersonationSessionsOverlay } from "./appV2/ImpersonationSessions/ImpersonationSessionsOverlay";
import { ImpersonationSessionsProvider } from "./appV2/ImpersonationSessions/ImpersonationSessionsProvider";
import { GlobalPollingProvider } from "./appV2/polling/useGlobalPolling";
import { datadogRum } from "@datadog/browser-rum";
import { IMPERSONATE_PATH } from "./appV2/Impersonate/paths";
import { DeepLinkProvider } from "./mobile/providers/DeepLinkProvider";
import { APP_V2_APP_EVENTS, logError } from "./appV2/lib/analytics";
import { signalLiveUpdateReady } from "./mobile/utils/liveUpdate.adapter";
import { Platform } from "./mobile/utils/platform";
import { REBRAND_PRIMARY_COLOR } from "./appV2/lib/Theme/rebrandConstants";
import { MobileSplashOverlay } from "./mobile/MobileSplash/MobileSplashOverlay";

const flagOverridePlugin = new FlagOverridePlugin();
const eventInterceptionPlugin = new EventInterceptionPlugin();

interface LaunchDarklyToolbarWrapperProps {
  children: ReactNode;
}

function LaunchDarklyToolbarWrapper({ children }: LaunchDarklyToolbarWrapperProps) {
  useLaunchDarklyToolbar({
    flagOverridePlugin,
    eventInterceptionPlugin,
    position: "bottom-right",
    enabled: isDevelopmentNodeEnvironment() && !Platform.isMobile,
  });

  return <>{children}</>;
}

const DocumentProcessorContainer = loadLazily(() =>
  import("./containers/documentProcessor/DocumentProcessorContainer").then((module) => ({
    default: module.DocumentProcessorContainer,
  }))
);

const ImpersonatePage = loadLazily(() =>
  import("./appV2/Impersonate/ImpersonatePage").then((module) => ({
    default: module.ImpersonatePage,
  }))
);

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root is not found");
}
const root = createRoot(container);

const { api, app } = meta();

window.global.api = api;
window.global.app = app;

if (Platform.isMobile) {
  document.body.style.backgroundColor = REBRAND_PRIMARY_COLOR;
}

function AppReadySignal() {
  useEffect(() => {
    window.__APP_READY__ = true;
  }, []);
  return null;
}

function AppWithSplash({ children }: { children: ReactNode }) {
  const [showSplash, setShowSplash] = useState(Platform.isMobile);

  return (
    <>
      {children}
      {showSplash && (
        <MobileSplashOverlay
          onFinished={() => {
            setShowSplash(false);
            document.body.style.backgroundColor = "";
          }}
        />
      )}
    </>
  );
}

void (async () => {
  // Signal live update readiness before any async initialization to avoid
  // the readyTimeout (10s) being consumed by LaunchDarkly network calls.
  if (Platform.isMobile) {
    signalLiveUpdateReady().catch((error: unknown) => {
      logError(APP_V2_APP_EVENTS.LIVE_UPDATE_READY_FAILURE, { error });
    });
  }

  const LDProvider = await asyncWithLDProvider({
    context: { anonymous: true, kind: LD_USER_KIND },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
    clientSideID: environmentConfig.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID,
    // Bound LD init so a slow start can't leave the app blank indefinitely.
    // Flag consumers use `useCbhFlag(..., { defaultValue })` and re-render
    // when LD resolves, so the rare gap is self-healing; the post-login
    // `isIdentifiedForCurrentUser` gate still protects flag-gated layouts.
    timeout: 5,
    options: {
      evaluationReasons: true,
      plugins: [flagOverridePlugin, eventInterceptionPlugin],
      inspectors: [
        {
          type: "flag-used",
          name: "flag-inspector",
          method: async (key: string, detail: LDEvaluationDetail) => {
            datadogRum.addFeatureFlagEvaluation(key, detail.value);
          },
        },
      ],
    },
  });

  root.render(
    <ErrorBoundary>
      <AppWithSplash>
        {/* we will replace apollo with react-query since react-query has better support for both REST and graphql */}
        <ApolloProvider client={GraphQlClient}>
          <QueryProvider>
            <Provider store={store}>
              <I18nProvider>
                <Helmet>
                  <title>Clipboard Workplace</title>
                </Helmet>
                <BrowserRouter basename={process.env.REACT_APP_BASE_PATH || "/"}>
                  <Switch>
                    <Route exact path={IMPERSONATE_PATH}>
                      <MaterialUiProviders>
                        <Suspense fallback={<FallbackLoader />}>
                          <ImpersonatePage />
                        </Suspense>
                      </MaterialUiProviders>
                    </Route>
                    <Route>
                      <QueryParamProvider
                        adapter={ReactRouter5Adapter}
                        options={{
                          searchStringToObject: parse,
                          objectToSearchString: stringify,
                        }}
                      >
                        <DeepLinkProvider>
                          <LDProvider>
                            <LaunchDarklyToolbarWrapper>
                              <ChatProvider>
                                <MaterialUiProviders>
                                  <GlobalPollingProvider>
                                    <ImpersonationSessionsProvider>
                                      <ImpersonationSessionsOverlay />
                                      <AppReadySignal />
                                      <Switch>
                                        {/* add Guest's path to satisfy Switch's requirement for Route direct children
                            https://github.com/remix-run/react-router/blob/v5/packages/react-router/modules/Switch.js#L27 */}
                                        <Guest
                                          path={[
                                            routes.workplaceGetAccess,
                                            routes.workplaceReferrals,
                                            routes.shiftBoard,
                                          ]}
                                        />
                                        <Route exact path={routes.verifyDocument}>
                                          <Suspense fallback={<FallbackLoader />}>
                                            <DocumentProcessorContainer />
                                          </Suspense>
                                        </Route>
                                        <App />
                                      </Switch>
                                    </ImpersonationSessionsProvider>
                                  </GlobalPollingProvider>
                                </MaterialUiProviders>
                              </ChatProvider>
                            </LaunchDarklyToolbarWrapper>
                          </LDProvider>
                        </DeepLinkProvider>
                      </QueryParamProvider>
                    </Route>
                  </Switch>
                </BrowserRouter>
              </I18nProvider>
            </Provider>
          </QueryProvider>
        </ApolloProvider>
      </AppWithSplash>
    </ErrorBoundary>
  );
})();
